/**
 * 消息详情弹窗
 * @author ritaxxu
 */
import React from 'react'
import { Modal, Button, Tag, message as antdMessage } from 'antd'
import { PaperClipOutlined, CheckCircleOutlined, ExclamationCircleFilled } from '@ant-design/icons'
import { cosGetObjectUrl } from '@library/cosUtils'
import RichContent from '../RichContent'
import {
  IInnerMsgItem,
  IInnerMsgAttachment,
  InnerMsgLevel,
  InnerMsgReadStatus,
} from '@types'

interface MessageDetailModalProps {
  visible: boolean
  message: IInnerMsgItem | null
  onClose: () => void
  /** 重要消息的【标为已读】按钮回调（仅 level=2 且未读时显示） */
  onMarkRead: () => void
}

/** 下载附件（走 COS 签名 URL） */
async function downloadAttachment (att: IInnerMsgAttachment) {
  const [url, err] = await cosGetObjectUrl(att.cosKey)
  if (err || !url) {
    antdMessage.error(`下载失败：${err?.message || '获取地址失败'}`)
    return
  }
  const a = document.createElement('a')
  a.href = url
  a.download = att.name
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

const MessageDetailModal: React.FC<MessageDetailModalProps> = ({
  visible,
  message,
  onClose,
  onMarkRead,
}) => {
  if (!message) return null

  const isImportant = message.level === InnerMsgLevel.Important
  const isUnread = message.readStatus === InnerMsgReadStatus.Unread
  const attachments = message.attachments || []

  return (
    <Modal
      title={message.title}
      open={visible}
      onCancel={onClose}
      width={720}
      footer={
        <div className="message-detail__footer">
          <span className="message-detail__read-time">
            {message.readTime ? `阅读时间：${message.readTime}` : ''}
          </span>
          <div>
            {isImportant && isUnread && (
              <Button
                danger
                className="notification-card-mark-btn"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  onMarkRead()
                  onClose()
                }}
              >
                标为已读
              </Button>
            )}
            <Button onClick={onClose} style={{ marginLeft: 8 }}>关闭</Button>
          </div>
        </div>
      }
    >
      <div className="message-detail">
        {/* 级别 + 时间卡片 */}
        <div className="message-detail__meta-card">
          {isImportant
            ? <Tag color="red" icon={<ExclamationCircleFilled />}>重要</Tag>
            : <Tag>普通</Tag>}
          {message.sourceType === 2 && message.sourceRuleName && (
            <Tag color="blue">{message.sourceRuleName}</Tag>
          )}
          <span className="message-detail__time">{message.sentAt}</span>
        </div>

        {/* 正文 + 附件卡片 */}
        <div className="message-detail__content-card">
          <RichContent
            className="message-detail__content notification-detail-content"
            html={message.content}
          />

          {attachments.length > 0 && (
            <>
              <hr className="message-detail__hr" />
              <div className="message-detail__attach">
                <span className="message-detail__attach-label">附件：</span>
                <div className="message-detail__attach-list">
                  {attachments.map((att) => (
                    <span
                      key={att.cosKey}
                      className="message-detail__attach-link"
                      title={att.name}
                      onClick={() => downloadAttachment(att)}
                    >
                      <PaperClipOutlined />
                      {att.name}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default MessageDetailModal
